"use client";

import React, { useEffect, useRef, useState } from "react";
import { Search, PenTool, Code, FileText, Rocket, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  id: 1,
  title: "Discovery",
  description: "We start by understanding your business, goals, and target audience. We analyze your competitors and define a strategy that ensures your new website attracts the right customers.",
  icon: Search
}, {
  id: 2,
  title: "Content",
  description: "We integrate your text and images, optimizing them for SEO. If you've chosen our Content Creation Add-On, we'll incorporate your custom photography and video here.",
  icon: FileText
}, {
  id: 3,
  title: "Design",
  description: "Our designers create a modern, high-converting layout tailored to your brand. We focus on user experience, ensuring your site looks premium and functions perfectly on all devices.",
  icon: PenTool
}, {
  id: 4,
  title: "Build",
  description: "We develop your site using clean, semantic code. This phase includes setting up the CMS, ensuring fast load times, and implementing mobile-responsive frameworks.",
  icon: Code
}, {
  id: 5,
  title: "Launch",
  description: "After rigorous testing and your final approval, we launch your website. We handle the domain connection, SSL setup, and ensure everything is live and running smoothly.",
  icon: Rocket
}];
export default function OurProcess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll();
  return;
}